"use client"

import { useEffect, useState } from "react"
import { Scale, FileText, AlertCircle, CheckCircle } from "lucide-react"

export default function TermsPage() {
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    setLoaded(true)
    window.scrollTo(0, 0)
  }, [])

  return (
    <div className="flex min-h-screen flex-col bg-[#1a1c1e] text-white">
      <main className="flex-1">
        {/* Hero Section */}
        <section
          className={`w-full py-16 md:py-24 bg-gradient-to-br from-[#1a1c1e] via-[#252729] to-[#1a1c1e] transition-all duration-1000 ${loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}
        >
          <div className="container px-4 md:px-6">
            <div className="flex flex-col items-center space-y-6 text-center">
              <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-[#e57312]/10 backdrop-blur-sm border border-[#e57312]/20">
                <Scale className="h-10 w-10 text-[#e57312]" />
              </div>
              <div className="space-y-4">
                <h1 className="text-4xl font-bold tracking-tighter sm:text-5xl md:text-6xl bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                  Terms of <span className="text-[#e57312]">Service</span>
                </h1>
                <p className="max-w-[800px] text-xl text-gray-300">
                  Last Updated: February 2, 2026
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="w-full py-16 bg-[#1a1c1e]">
          <div className="container px-4 md:px-6">
            <div className="mx-auto max-w-4xl space-y-12">
              {/* Introduction */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <FileText className="h-6 w-6 text-[#e57312]" />
                  <h2 className="text-3xl font-bold text-white">Introduction</h2>
                </div>
                <div className="rounded-xl border border-[#2c2e30] bg-[#252729]/50 p-6 space-y-4">
                  <p className="text-gray-300 leading-relaxed">
                    Welcome to HSVE Consultation Services ("HSVE," "we," "our," or "us"). These Terms of Service
                    ("Terms") govern your access to and use of our website, services, products, and any related
                    offerings (collectively, the "Services").
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    By accessing or using our Services, you agree to be bound by these Terms. If you do not agree to
                    these Terms, please do not use our Services.
                  </p>
                </div>
              </div>

              {/* Services Description */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="h-6 w-6 text-[#e57312]" />
                  <h2 className="text-3xl font-bold text-white">1. Services Description</h2>
                </div>
                <div className="rounded-xl border border-[#2c2e30] bg-[#252729]/50 p-6 space-y-4">
                  <p className="text-gray-300 leading-relaxed">
                    HSVE provides professional IT consultation services, including but not limited to:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                    <li>Proxmox Virtual Environment setup and configuration</li>
                    <li>Custom virtual machine templates and deployment</li>
                    <li>Virtualization platform migration services</li>
                    <li>Technical training and support</li>
                    <li>Digital products and downloadable templates</li>
                  </ul>
                  <p className="text-gray-300 leading-relaxed">
                    The specific scope, deliverables, and timelines for consultation services will be outlined in
                    separate service agreements or statements of work.
                  </p>
                </div>
              </div>

              {/* Account Registration */}
              <div className="space-y-4">
                <h2 className="text-3xl font-bold text-white">2. Account Registration</h2>
                <div className="rounded-xl border border-[#2c2e30] bg-[#252729]/50 p-6 space-y-4">
                  <p className="text-gray-300 leading-relaxed">
                    To access certain features of our Services, you may be required to create an account. You agree to:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                    <li>Provide accurate, current, and complete information during registration</li>
                    <li>Maintain and promptly update your account information</li>
                    <li>Maintain the security of your password and account credentials</li>
                    <li>Notify us immediately of any unauthorized use of your account</li>
                    <li>Accept responsibility for all activities that occur under your account</li>
                  </ul>
                  <p className="text-gray-300 leading-relaxed">
                    We reserve the right to suspend or terminate accounts that violate these Terms or engage in
                    fraudulent, abusive, or illegal activities.
                  </p>
                </div>
              </div>

              {/* Payment Terms */}
              <div className="space-y-4">
                <h2 className="text-3xl font-bold text-white">3. Payment Terms</h2>
                <div className="rounded-xl border border-[#2c2e30] bg-[#252729]/50 p-6 space-y-4">
                  <p className="text-gray-300 leading-relaxed">
                    <strong className="text-white">Pricing:</strong> All prices are displayed in British Pounds (GBP)
                    unless otherwise stated. Prices are subject to change without notice, but changes will not affect
                    orders already placed.
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    <strong className="text-white">Payment Processing:</strong> Payments are processed through secure
                    third-party payment processors. You agree to pay all fees and charges associated with your
                    purchases.
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    <strong className="text-white">Invoicing:</strong> For consultation services, invoices will be
                    issued according to the terms specified in your service agreement. Payment is typically due within
                    14 days of invoice date unless otherwise agreed.
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    <strong className="text-white">Taxes:</strong> You are responsible for any applicable taxes,
                    duties, or similar charges related to your purchase, except for taxes based on our net income.
                  </p>
                </div>
              </div>

              {/* Licenses and Digital Products */}
              <div className="space-y-4">
                <h2 className="text-3xl font-bold text-white">4. Licenses and Digital Products</h2>
                <div className="rounded-xl border border-[#2c2e30] bg-[#252729]/50 p-6 space-y-4">
                  <p className="text-gray-300 leading-relaxed">
                    <strong className="text-white">License Grants:</strong> Upon purchase of digital products or
                    templates, we grant you a non-exclusive, non-transferable, revocable license to use the product in
                    accordance with the license terms provided at the time of purchase.
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    <strong className="text-white">Restrictions:</strong> Unless explicitly permitted, you may not:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                    <li>Redistribute, resell, or sublicense our digital products</li>
                    <li>Remove or modify any copyright, trademark, or proprietary notices</li>
                    <li>Reverse engineer, decompile, or disassemble our products</li>
                    <li>Use our products for any unlawful purpose</li>
                  </ul>
                  <p className="text-gray-300 leading-relaxed">
                    <strong className="text-white">License Validation:</strong> Some products may require online
                    license validation. You agree to allow such validation checks and understand that failure to
                    validate may result in service interruption.
                  </p>
                </div>
              </div>

              {/* Refund Policy */}
              <div className="space-y-4">
                <h2 className="text-3xl font-bold text-white">5. Refund Policy</h2>
                <div className="rounded-xl border border-[#2c2e30] bg-[#252729]/50 p-6 space-y-4">
                  <p className="text-gray-300 leading-relaxed">
                    <strong className="text-white">Digital Products:</strong> Due to the nature of digital products,
                    all sales are generally final. However, we may provide refunds at our sole discretion in cases of:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                    <li>Technical defects that prevent the product from functioning as described</li>
                    <li>Duplicate purchases made in error</li>
                    <li>Products that materially differ from their description</li>
                  </ul>
                  <p className="text-gray-300 leading-relaxed">
                    <strong className="text-white">Consultation Services:</strong> Refund eligibility for consultation
                    services will be determined based on the terms outlined in your specific service agreement.
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    Refund requests must be submitted within 14 days of purchase. To request a refund, contact us at
                    the email address provided in the Contact section.
                  </p>
                </div>
              </div>

              {/* Intellectual Property */}
              <div className="space-y-4">
                <h2 className="text-3xl font-bold text-white">6. Intellectual Property</h2>
                <div className="rounded-xl border border-[#2c2e30] bg-[#252729]/50 p-6 space-y-4">
                  <p className="text-gray-300 leading-relaxed">
                    All content, features, and functionality of our Services, including but not limited to text,
                    graphics, logos, icons, images, audio clips, digital downloads, and software, are the exclusive
                    property of HSVE or our licensors and are protected by international copyright, trademark, patent,
                    trade secret, and other intellectual property laws.
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    You may not reproduce, distribute, modify, create derivative works of, publicly display, or
                    otherwise exploit any of our proprietary content without our express written permission.
                  </p>
                </div>
              </div>

              {/* User Conduct */}
              <div className="space-y-4">
                <h2 className="text-3xl font-bold text-white">7. User Conduct</h2>
                <div className="rounded-xl border border-[#2c2e30] bg-[#252729]/50 p-6 space-y-4">
                  <p className="text-gray-300 leading-relaxed">You agree not to:</p>
                  <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                    <li>Use our Services for any unlawful purpose or in violation of these Terms</li>
                    <li>Attempt to gain unauthorized access to any part of our Services</li>
                    <li>Interfere with or disrupt the operation of our Services</li>
                    <li>Upload or transmit viruses, malware, or other malicious code</li>
                    <li>Impersonate any person or entity or misrepresent your affiliation</li>
                    <li>Collect or harvest information about other users without their consent</li>
                    <li>Engage in any activity that could damage our reputation or business</li>
                  </ul>
                </div>
              </div>

              {/* Disclaimers and Warranties */}
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <AlertCircle className="h-6 w-6 text-[#e57312]" />
                  <h2 className="text-3xl font-bold text-white">8. Disclaimers and Warranties</h2>
                </div>
                <div className="rounded-xl border border-[#2c2e30] bg-[#252729]/50 p-6 space-y-4">
                  <p className="text-gray-300 leading-relaxed">
                    OUR SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS
                    OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
                    PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    We do not warrant that our Services will be uninterrupted, secure, or error-free, or that any
                    defects will be corrected. You use our Services at your own risk.
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    While we strive to provide accurate and reliable consultation services, we do not guarantee specific
                    results or outcomes. Technical implementations depend on numerous factors beyond our control.
                  </p>
                </div>
              </div>

              {/* Limitation of Liability */}
              <div className="space-y-4">
                <h2 className="text-3xl font-bold text-white">9. Limitation of Liability</h2>
                <div className="rounded-xl border border-[#2c2e30] bg-[#252729]/50 p-6 space-y-4">
                  <p className="text-gray-300 leading-relaxed">
                    TO THE MAXIMUM EXTENT PERMITTED BY LAW, HSVE AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS
                    SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES,
                    INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, USE, OR GOODWILL, ARISING OUT OF OR RELATED TO
                    YOUR USE OF OUR SERVICES.
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    OUR TOTAL LIABILITY TO YOU FOR ANY CLAIMS ARISING OUT OF OR RELATED TO THESE TERMS OR OUR SERVICES
                    SHALL NOT EXCEED THE AMOUNT YOU PAID TO US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR £100,
                    WHICHEVER IS GREATER.
                  </p>
                </div>
              </div>

              {/* Indemnification */}
              <div className="space-y-4">
                <h2 className="text-3xl font-bold text-white">10. Indemnification</h2>
                <div className="rounded-xl border border-[#2c2e30] bg-[#252729]/50 p-6 space-y-4">
                  <p className="text-gray-300 leading-relaxed">
                    You agree to indemnify, defend, and hold harmless HSVE and its officers, directors, employees,
                    agents, and affiliates from and against any claims, liabilities, damages, losses, costs, expenses,
                    or fees (including reasonable attorneys' fees) arising out of or relating to:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                    <li>Your use of our Services</li>
                    <li>Your violation of these Terms</li>
                    <li>Your violation of any rights of another party</li>
                    <li>Your violation of any applicable laws or regulations</li>
                  </ul>
                </div>
              </div>

              {/* Term and Termination */}
              <div className="space-y-4">
                <h2 className="text-3xl font-bold text-white">11. Term and Termination</h2>
                <div className="rounded-xl border border-[#2c2e30] bg-[#252729]/50 p-6 space-y-4">
                  <p className="text-gray-300 leading-relaxed">
                    These Terms remain in effect while you use our Services. We may suspend or terminate your access to
                    our Services at any time, with or without cause or notice, if we believe you have violated these
                    Terms or for any other reason at our sole discretion.
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    Upon termination, your right to use our Services will immediately cease. Sections of these Terms
                    that by their nature should survive termination shall survive, including but not limited to
                    intellectual property provisions, disclaimers, and limitations of liability.
                  </p>
                </div>
              </div>

              {/* Governing Law */}
              <div className="space-y-4">
                <h2 className="text-3xl font-bold text-white">12. Governing Law and Dispute Resolution</h2>
                <div className="rounded-xl border border-[#2c2e30] bg-[#252729]/50 p-6 space-y-4">
                  <p className="text-gray-300 leading-relaxed">
                    These Terms shall be governed by and construed in accordance with the laws of England and Wales,
                    without regard to its conflict of law provisions.
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    Any disputes arising out of or relating to these Terms or our Services shall be resolved through
                    good faith negotiations. If negotiations fail, disputes shall be subject to the exclusive
                    jurisdiction of the courts of England and Wales.
                  </p>
                </div>
              </div>

              {/* Changes to Terms */}
              <div className="space-y-4">
                <h2 className="text-3xl font-bold text-white">13. Changes to Terms</h2>
                <div className="rounded-xl border border-[#2c2e30] bg-[#252729]/50 p-6 space-y-4">
                  <p className="text-gray-300 leading-relaxed">
                    We reserve the right to modify these Terms at any time. We will notify you of material changes by
                    posting the updated Terms on our website and updating the "Last Updated" date. Your continued use of
                    our Services after such changes constitutes your acceptance of the new Terms.
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    We encourage you to review these Terms periodically to stay informed about our terms and conditions.
                  </p>
                </div>
              </div>

              {/* Contact Information */}
              <div className="space-y-4">
                <h2 className="text-3xl font-bold text-white">14. Contact Information</h2>
                <div className="rounded-xl border border-[#2c2e30] bg-[#252729]/50 p-6 space-y-4">
                  <p className="text-gray-300 leading-relaxed">
                    If you have any questions, concerns, or feedback regarding these Terms, please contact us:
                  </p>
                  <div className="space-y-2 text-gray-300">
                    <p>
                      <strong className="text-white">HSVE Consultation Services</strong>
                    </p>
                    <p>Email: support@hsve.cc</p>
                    <p>Website: https://hsve.cc</p>
                  </div>
                </div>
              </div>

              {/* Severability */}
              <div className="space-y-4">
                <h2 className="text-3xl font-bold text-white">15. Severability</h2>
                <div className="rounded-xl border border-[#2c2e30] bg-[#252729]/50 p-6 space-y-4">
                  <p className="text-gray-300 leading-relaxed">
                    If any provision of these Terms is found to be unenforceable or invalid, that provision shall be
                    limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in
                    full force and effect.
                  </p>
                </div>
              </div>

              {/* Entire Agreement */}
              <div className="space-y-4">
                <h2 className="text-3xl font-bold text-white">16. Entire Agreement</h2>
                <div className="rounded-xl border border-[#2c2e30] bg-[#252729]/50 p-6 space-y-4">
                  <p className="text-gray-300 leading-relaxed">
                    These Terms constitute the entire agreement between you and HSVE regarding your use of our Services
                    and supersede any prior agreements, understandings, or representations, whether written or oral.
                  </p>
                </div>
              </div>

              {/* Acknowledgment */}
              <div className="rounded-xl border-2 border-[#e57312]/50 bg-gradient-to-br from-[#e57312]/10 to-[#252729]/50 p-8 space-y-4">
                <h3 className="text-2xl font-bold text-[#e57312]">Acknowledgment</h3>
                <p className="text-gray-300 leading-relaxed">
                  BY USING OUR SERVICES, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE
                  TERMS OF SERVICE.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  )
}
